import cloud from 'd3-cloud';
import { each, map, sortBy, reverse } from 'lodash/fp';
import app from '../../angular_app.es6';
import d3 from '../../../common_utils/d3';

app.directive('expressWordCloud', [ () => {
  return {
    restrict: 'E',
    scope: {
      words: '=',
      noFill: '@',
      showProjectColor: '@',
      color: '@',
      onWordClick: '&',
      rangeMax: '@',
      rangeMin: '@',
      size: '=?',
      fontFamily: '@',
      cancelFontSetup: '@',
      screenshoots: '@'
    },

    link: ($scope, $element, $attrs) => {
      const COLORS = {
        green: $attrs.positiveColor || '#00812e',
        yellow: $attrs.neutralColor || '#ffbc3c',
        red: $attrs.negativeColor || '#ed1c24'
      };

      const rangeConstants = {
        defaultWidth: 800,
        defaultHeight: 400,
        maxMargin: 7,
        rangeMaxNum: $scope.screenshoots ? 10 : 6,
        rangeMinNum: $scope.screenshoots ? 70 : 30,
        reductionKoefNum: $scope.screenshoots ? 5 : 20
      };

      $scope.state = {
        fontFamily: $scope.fontFamily || "Arial Regular, Arial, Helvetica Neue, Helvetica, sans-serif"
      };

      const maxMargin = rangeConstants.maxMargin;
      let layout;

      const initLayout = (force = false) => {
        const width = $attrs.width || ($scope.size && $scope.size.width) || rangeConstants.defaultWidth;
        const height = $attrs.height || ($scope.size && $scope.size.height) || rangeConstants.defaultHeight;

        const defineRangeMax = () => Math.round(width / rangeConstants.rangeMaxNum);
        const defineRangeMin = () => Math.round(width / rangeConstants.rangeMinNum);

        if (force) {
          $scope.rangeMax = defineRangeMax();
          $scope.rangeMin = defineRangeMin();
        } else {
          $scope.rangeMax = $scope.rangeMax || defineRangeMax();
          $scope.rangeMin = $scope.rangeMin || defineRangeMin();
        }

        const range = $scope.rangeMax - $scope.rangeMin;
        const reducingCoef = Math.floor(range / rangeConstants.reductionKoefNum);

        return cloud()
          .size([ width, height ])
          .padding(8)
          .rotate(0)
          .fontSize((d) => d.size)
          .text((d) => { return d.text; })
          .on('end', (output) => {
            // console.log('output', map('size', output));
            // To avoid outputting not all words
            if ($scope.words && $scope.words.length !== output.length && $scope.rangeMax > $scope.rangeMin) {
              $scope.rangeMax = Math.max($scope.rangeMax - reducingCoef, $scope.rangeMin);
              setRangesAndRun();
            } else {
              draw(output);
            }
          });
      };

      const draw = (words) => {
        let x0 = -1, x1 = -1, y0 = -1, y1 = -1;

        words.forEach((d) => {
          if (x0 === -1 || (d.x + d.x0) < x0) {
            x0 = d.x + d.x0;
          }

          if (x1 === -1 || (d.x + d.x0 + d.width) > x1) {
            x1 = d.x + d.x0 + d.width;
          }

          if (y0 === -1 || (d.y + d.y0) < y0) {
            y0 = d.y + d.y0;
          }

          if (y1 === -1 || (d.y + d.y0 + d.height / 2 + d.padding) > y1) {
            y1 = d.y + d.y0 + d.height / 2 + d.padding;
          }
        });

        const kx = (x1 - x0 === 0 ? 1 : layout.size()[0] / (x1 - x0)),
          ky = (y1 - y0 === 0 ? 1 : layout.size()[1] / (y1 - y0)),
          k = Math.min(kx, ky) * 0.96;
        const sx = -x0 + (layout.size()[0] - (x1 - x0)) / 2;
        const sy = -y0 + (layout.size()[1] - (y1 - y0)) / 2;


        const svg = d3.select($element[0]);

        svg.select('svg').remove();
        const svgNode = svg.append('svg')
            .attr("width", layout.size()[0])
            .attr("height", layout.size()[1]),
          g = svgNode.append("g")
            .attr("transform", `translate(${sx} ${sy}) scale(${k})`)
            .attr("transform-origin", `${(x1 + x0) / 2} ${(y1 + y0) / 2}`),
          nodes = g.selectAll("text").data(words).enter();

        const list = nodes.append("text")
          .style("font-size", (d) => `${d.size}px`)
          .style("font-family", $scope.state.fontFamily)
          .attr("font-family", $scope.state.fontFamily)
          .attr("text-anchor", "middle")
          .attr("transform", (d) => `translate(${[ d.x, d.y ]}) rotate(${d.rotate})`)
          .text((d) => d.text);

        if ($scope.noFill !== 'true') {
          if ($scope.showProjectColor === 'true') {
            list.style("fill", (d) => $scope.color || COLORS[d.project_color] || d.project_color || "#000000");
          } else {
            list.style("fill", (d) => $scope.color || COLORS[d.color] || COLORS[d.status_name] || d.color || "#000000");
          }
        } else {
          list.style("fill", (_) => $attrs.emptyColor || "#000000");
        }
        if ($scope.onWordClick) {
          list.on("click", (d) => $scope.onWordClick({ text: d.text }));
        }
      };

      const setRangesAndRun = () => {
        const words = reverse(sortBy('total')($scope.words));

        const scale = d3.scale.linear()
          .domain([ d3.min(words, (d) => d.total) - 0.1, d3.max(words, (d) => d.total) ])
          .range([ $scope.rangeMin, $scope.rangeMax ]);

        // Setup sizes with max margin between words.
        const eachWithIndex = each.convert({ 'cap': false });
        eachWithIndex((w, i) => {
          w.size = Math.floor(scale(w.total));
          const prevW = words[i - 1];
          if (prevW) {
            if (prevW.size - maxMargin > w.size) {
              prevW.size = w.size + maxMargin;
            }
          }
        })(words);

        layout.words(words);
        layout.start();
      };

      $scope.$watch(
        () => {
          return $scope.words ? map((w) => w.text, $scope.words).join(',') : null;
        },
        (newWords, oldWords) => {
          if (!newWords && !oldWords) {
            return;
          }
          if (!layout) {
            layout = initLayout(true);
          }
          setRangesAndRun();
        }
      );


      $scope.$watch('noFill', () => {
        if (layout) {
          layout.start();
        }
      });

      $scope.$watch('showProjectColor', () => {
        if (layout) {
          layout.start();
        }
      });

      $scope.$watch('size', (value) => {
        if (!value) {
          return;
        }

        layout = initLayout(true);
        setRangesAndRun();
      }, true);
    }
  };
} ]);
